import { IUsersService } from './IUsersService';
import { AccessLevel, Gender, IUser } from './IUser';
import { Api } from '../../Api';
import { IExalusConnectionService } from '../IExalusConnectionService';
import { ExalusConnectionService } from '../ExalusConnectionService';
import { DataFrame, Method, Status } from '../../DataFrame';
import { SessionService, UserData } from '../Session/SessionService';
import { User } from './User';
import { DependencyContainer } from '../../DependencyContainer';
import { ResponseResult } from '../FieldChangeResult';
import { Picture } from '../Pictures/Picture';
import { IPicturesService } from '../Pictures/IPicturesService';
import { PicturesService } from '../Pictures/PicturesService';
import { ImageType, IPicture, IPictureInfo } from '../Pictures/IPicture';
import { ISessionService } from '../Session/ISessionService';
import { WebApiCacheService } from '../WebApi/WebApiCacheService';
import { IWebApiCacheService } from '../WebApi/IWebApiCacheService';

export class UsersService implements IUsersService {
    private _connection: IExalusConnectionService | null = null;
    private _pictureService: IPicturesService;
    private _sessionService: ISessionService;
    public static readonly ServiceName: string = "UsersService";

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._pictureService = Api.Get<IPicturesService>(PicturesService.ServiceName);
        this._sessionService = Api.Get<ISessionService>(SessionService.ServiceName);
    }
    
    CanEditUser(user: IUser): boolean {
        const us = this._sessionService.User;
        
        if(user.Guid === us?.Guid)
            return true;

        return this.CanEditUsersOfAccessLevel(user.AccessLevel);
    }

    CanEditUsersOfAccessLevel(level: AccessLevel): boolean {
        const currentlyLoggedUserLevel = this._sessionService.User?.AccessLevel;

        if (currentlyLoggedUserLevel === undefined)
            return false;

        switch (level) {
            case AccessLevel.Support:
                return currentlyLoggedUserLevel === AccessLevel.Support;

            case AccessLevel.Installator:
                return currentlyLoggedUserLevel >= AccessLevel.Installator;

            case AccessLevel.Admin:
                return currentlyLoggedUserLevel >= AccessLevel.Admin;

            default:
                return currentlyLoggedUserLevel >= AccessLevel.Admin;
        }
    }

    GetServiceName(): string {
        return UsersService.ServiceName;
    }

    async GetUsersAsync(): Promise<IUser[]> {
        const result = await this._connection?.SendAndWaitForResponseAsync<UserData[]>(new GetUsersListRequest(), 20000, false);

        if (result === null || result === undefined)
            return Promise.resolve([]);

        if (result?.Status == Status.OK) {
            if (result.Data != null) {
                const users: User[] = [];

                result.Data.forEach(element => {
                    var user = new User();
                    user.AccessLevel = element.AccessLevel;
                    user.Email = element.Email;
                    user.Gender = element.Gender;
                    user.Guid = element.Guid;
                    user.IsAccountOnline = element.IsAccountOnline;
                    user.Name = element.Name;
                    user.IsActive = element.IsActive;
                    user.IsBanned = element.IsBanned;
                    user.Phone = element.Phone;
                    user.Surname = element.Surname;
                    users.push(user);
                });

                return users;
            }
            else
                return Promise.resolve([]);
        }
        else if (result?.Status == Status.NoData)
            return Promise.resolve([]);
        else {
            DependencyContainer.Log?.Debug(UsersService.ServiceName, `Failed to retrieve users, error: ${result.Status}`);
            throw new Error(`Failed to retrieve users, error: ${result.Status}`);
        }
    }

    async GetUserAsync(id?: string): Promise<IUser | null> {

        if (id === "")
            return Promise.resolve(null);
        else if (id == null)
            id = "";

        const result = await this._connection?.SendAndWaitForResponseAsync<UserData>(new GetUserRequest(id), 20000, false);

        if (result == null || result.Data == null)
            return Promise.resolve(null);

        if (result.Status != Status.OK) {
            DependencyContainer.Log?.Debug(UsersService.ServiceName, `Failed to retrieve user, error: ${result.Status}`);
            return Promise.resolve(null);
        }

        const user = new User();
        user.AccessLevel = result.Data.AccessLevel;
        user.Email = result.Data.Email;
        user.Gender = result.Data.Gender;
        user.Guid = result.Data.Guid;
        user.IsAccountOnline = result.Data.IsAccountOnline;
        user.Name = result.Data.Name;
        user.IsActive = result.Data.IsActive;
        user.IsBanned = result.Data.IsBanned;
        user.Phone = result.Data.Phone;
        user.Surname = result.Data.Surname;

        return user;
    }

    async CreateUserAsync(user: IUser, password: string): Promise<ResponseResult<Status>> {

        if (user.Guid != null && user.Guid != "")
            return new ResponseResult(Status.WrongData, "GuidMustBeEmpty");

        const userRequest = new UserDataFrame();
        userRequest.Name = user.Name.trim();
        userRequest.Surname = user.Surname.trim();
        userRequest.Email = user.Email.trim();
        userRequest.Password = password.trim();
        userRequest.Phone = user.Phone.trim();
        userRequest.Gender = user.Gender;
        userRequest.AccessLevel = user.AccessLevel;

        const result = await this._connection?.SendAndWaitForResponseAsync<string>(new SetUserRequest(userRequest), 20000, false);
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

        if (result == null || result.Status == null)
            return new ResponseResult(Status.FatalError, "UnknownError");

        if (result.Data == null)
            result.Data = "";

        return new ResponseResult(result.Status, result.Data);
    }

    async UpdateUserAsync(user: IUser): Promise<ResponseResult<Status>> {

        if (user.Guid == null || user.Guid == "")
            return new ResponseResult(Status.WrongData, "GuidCannotBeEmpty");

        const userRequest = new UserDataFrame();
        userRequest.Guid = user.Guid;
        userRequest.Name = user.Name.trim();
        userRequest.Surname = user.Surname.trim();
        userRequest.Email = user.Email.trim();
        userRequest.Phone = user.Phone.trim();
        userRequest.Gender = user.Gender;
        userRequest.AccessLevel = user.AccessLevel;

        const result = await this._connection?.SendAndWaitForResponseAsync<string>(new SetUserRequest(userRequest), 20000, false);
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

        if (result == null || result.Status == null)
            return new ResponseResult(Status.FatalError, "UnknownError");

        if (result.Data == null)
            result.Data = "";

        return new ResponseResult(result.Status, result.Data);
    }

    async ChangePasswordAsync(user: IUser, password: string): Promise<ResponseResult<Status>> {

        if (user.Guid == null || user.Guid == "")
            return new ResponseResult(Status.WrongData, "GuidCannotBeEmpty");

        const currentUser = await this.GetUserAsync(user.Guid);

        if (currentUser == null)
            return new ResponseResult(Status.ResourceIsNotAvailable, "CannotGetUser")

        const passRequest = new UserDataFrame();
        passRequest.Guid = user.Guid;
        passRequest.Password = password;
        passRequest.AccessLevel = currentUser.AccessLevel;
        passRequest.Email = currentUser.Email;

        const result = await this._connection?.SendAndWaitForResponseAsync<string>(new SetUserRequest(passRequest), 20000, false);

        if (result == null || result.Status == null)
            return new ResponseResult(Status.FatalError, "UnknownError");

        if (result.Data == null)
            result.Data = "";

        return new ResponseResult(result.Status, result.Data);
    }

    async DeleteUserAsync(id?: string): Promise<Status> {

        if (id === "")
            return Status.WrongData;
        else if (id == null)
            id = "";

        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new DeleteUserRequest(id), 20000, false);
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if (result == null || result.Status == null)
                return Status.FatalError;

            if (result.Status != Status.OK)
                DependencyContainer.Log?.Debug(UsersService.ServiceName, `Failed to delete user, error: ${result.Status}`);

            return result.Status;
        } catch (error) {
            DependencyContainer.Log?.Error(UsersService.ServiceName, `Failed to delete user: ${error}`);
            return Status.FatalError;
        }
    }

    async GetUserProfilePictureAsync(user: IUser): Promise<IPicture | ResponseResult<Status>> {

        if (user.Guid == null || user.Guid == "")
            return new ResponseResult(Status.WrongData, "GuidCannotBeEmpty");

        const picturesInfo = await this._pictureService.GetPicturesInfoAsync();

        if ((picturesInfo as ResponseResult<Status>).Type != null)
            return picturesInfo as ResponseResult<Status>;

        const pictureToGet = (picturesInfo as IPictureInfo[]).find(pInfo => pInfo.OwnerIdentity == "profilePicture" && pInfo.OwnerGuid == user.Guid);

        if (pictureToGet != null) {
            const profilePicture = await this._pictureService.GetPictureAsync(pictureToGet.Guid);
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if ((profilePicture as ResponseResult<Status>).Type != null)
                return profilePicture as ResponseResult<Status>;
            else return profilePicture as IPicture;
        }
        else {
            return new ResponseResult<Status>(Status.ResourceDoesNotExists, "PictureNotFound");
        }
    }

    async SetUserProfilePictureAsync(user: IUser, base64PictureString: string): Promise<ResponseResult<Status>> {

        if (user.Guid == null || user.Guid == "")
            return new ResponseResult(Status.WrongData, "GuidCannotBeEmpty");

        let picture = new Picture();
        picture.ImageType = ImageType.Person;
        picture.OwnerGuid = user.Guid;
        picture.OwnerIdentity = "profilePicture";
        picture.Base64Image = base64PictureString;

        const currentPicture = await this.GetUserProfilePictureAsync(user);
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

        //trying to add when not exists
        if ((currentPicture as ResponseResult<Status>).Type == Status.ResourceDoesNotExists) {
            const result = await this._pictureService.AddPictureAsync(picture);

            switch (result) {
                case Status.OK:
                    return new ResponseResult(Status.OK, "PictureSet");
                default:
                    return new ResponseResult(result, 'PictureServiceError');
            }

        }
        else if ((currentPicture as ResponseResult<Status>).Type != null) {
            return new ResponseResult(Status.Error, "ErrorWhenTryingToCheckIfCurrentProfilePictureExists");
        }
        //trying to edit
        else {
            picture.Guid = (currentPicture as IPicture).Guid;
            picture.OwnerGuid = (currentPicture as IPicture).OwnerGuid;

            const result = await this._pictureService.EditPictureAsync(picture);
            switch (result) {
                case Status.OK:
                    return new ResponseResult(Status.OK, "PictureSet");
                default:
                    return new ResponseResult(result, 'PictureServiceError');
            }
        }
    }
}

class GetUserRequest extends DataFrame<string>
{
    constructor(userGuid: string) {
        super();
        this.Resource = "/users/user";
        this.Method = Method.Get;
        this.Data = userGuid;
    }
}

class UserDataFrame {
    Guid: string = "";
    Name: string = "";
    Surname: string = "";
    Email: string = "";
    Password: string = "";
    Phone: string = "";
    Gender: Gender = Gender.Unknown;
    AccessLevel: AccessLevel = AccessLevel.Any;
}

class SetUserRequest extends DataFrame<UserDataFrame>
{
    constructor(user: UserDataFrame) {
        super();
        this.Resource = "/users/user";
        this.Method = Method.Put;
        this.Data = user;
    }
}

class GetUsersListRequest extends DataFrame<any>
{
    constructor() {
        super();
        this.Resource = "/users/list";
        this.Method = Method.Get;
    }
}

class DeleteUserRequest extends DataFrame<string>
{
    constructor(userGuid: string) {
        super();
        this.Resource = "/users/user";
        this.Method = Method.Delete;
        this.Data = userGuid;
    }
}
